import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DesignDocumentsService } from '../services/design-documents.service';
import type { DesignDocument, DesignDocumentFilters } from '../types/design-documents.types';
import { useState } from 'react';
import { toast } from 'sonner';

export function useDesignDocuments(companyId: string, initialFilters: DesignDocumentFilters = {}) {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<DesignDocumentFilters>({
    status: 'all',
    type: 'all',
    ...initialFilters
  });

  const query = useQuery({
    queryKey: ['design-documents', companyId, filters],
    queryFn: () => DesignDocumentsService.getDocuments(companyId, filters),
    enabled: !!companyId,
  });

  const statsQuery = useQuery({
    queryKey: ['design-document-stats', companyId, filters.projectId],
    queryFn: () => DesignDocumentsService.getDocumentStats(companyId, filters.projectId),
    enabled: !!companyId,
  });

  const uploadMutation = useMutation({
    mutationFn: (data: Partial<DesignDocument>) => DesignDocumentsService.uploadDocument(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['design-documents'] });
      toast.success('Tải lên hồ sơ thiết kế thành công');
    },
  });

  return {
    documents: query.data?.data || [],
    total: query.data?.total || 0,
    stats: statsQuery.data,
    isLoading: query.isLoading || statsQuery.isLoading,
    filters,
    setFilters,
    uploadDocument: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
  };
}

export function useDesignDocumentDetail(id?: string) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['design-documents', 'detail', id],
    queryFn: () => DesignDocumentsService.getDocumentDetail(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (updates: Partial<DesignDocument>) => DesignDocumentsService.updateDocument(id!, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['design-documents'] });
      queryClient.invalidateQueries({ queryKey: ['design-documents', 'detail', id] });
      toast.success('Cập nhật trạng thái hồ sơ thành công');
    },
  });

  return {
    document: query.data,
    isLoading: query.isLoading,
    updateDocument: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
  };
}
